/**
 * Wave 1 SEO cluster (part A): languages, email send, what not to include,
 * interests, references. Spanish and English. Merged from posts.js.
 * Mid-article CTA uses variant `mid` with copy that matches each guide.
 */

export default [
  {
    slug: 'idiomas-en-el-cv',
    date: '2026-08-22',
    category: 'language',
    related: ['cv-ingles-o-espanol', 'cv-para-master', 'adaptar-cv-a-una-oferta'],
    es: {
      title: 'Idiomas en el CV: niveles MCER (A1–C2) y cómo ponerlos',
      description:
        'Cómo listar idiomas en el currículum con niveles MCER en texto (no banderas). Qué certificado añadir, qué no inflar y cómo lo lee el ATS.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Si buscas cómo poner los idiomas en el CV, el error caro no es “olvidar el francés”. Es escribir “inglés alto”, poner tres banderas o un C1 que no puedes sostener en los primeros cinco minutos de llamada. En España y en buena parte de Europa, RR. HH. espera el Marco Común Europeo (MCER): A1 a C2. El ATS busca esas etiquetas, o el nombre del certificado. Un icono de bandera no es una keyword.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre dónde colocar la sección, cómo traducir “nativo / bilingüe / fluido” a A1–C2, qué hacer con un First o un B2 de hace diez años, y cuándo el idioma va en el resumen y no solo al pie. Si ya tienes un PDF, no hace falta empezar de cero: revisa si el nivel está en texto seleccionable. El flujo de ClarCV (clarcv.com) encaja aquí: subes el archivo, ves si el parseo come las banderas, y regeneras una plantilla con Idiomas en palabras.',
        },
        {
          type: 'p',
          text: 'No vas a “hackear” el marco europeo. Vas a ser preciso. Un B2 honesto con “reuniones con clientes UK” convence más que un C1 decorativo. En entrevista te pedirán que cuentes un proyecto en ese idioma. El chat no irá contigo. El certificado, tampoco, si no lo tienes.',
        },
        { type: 'h2', text: 'Usa MCER (A1–C2), no adjetivos' },
        {
          type: 'p',
          text: '“Inglés medio”, “francés fluido”, “alemán conversacional” no significan lo mismo para dos reclutadores. El MCER sí: A1/A2 (básico), B1/B2 (independiente), C1/C2 (competente). En un CV para España, escribe el idioma y el nivel: Inglés B2. Si tienes título, añádelo entre paréntesis una vez: Inglés B2 (First Certificate, 2022). No hace falta explicar el marco; quien contrata en Europa lo conoce.',
        },
        {
          type: 'ul',
          items: [
            'A1–A2: turismo, frases cortas. Casi nunca merece una línea propia salvo que la oferta pida “nociones”.',
            'B1: desenvolverse en el trabajo rutinario; aún no liderar una negociación difícil.',
            'B2: el umbral más pedido en ofertas españolas (“inglés alto”). Reuniones, email, documentación.',
            'C1: informes, clientes, presentaciones sin red. No lo pongas si dubitas en una call de screening.',
            'C2: casi nativo profesional. Reserva para quien trabaja el día a día en esa lengua.',
            'Nativo: tu lengua de escolarización o hogar. “Nativo” no es un C2 de academia.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: Inglés: alto. Francés: medio. Banderas. Después: Inglés C1 (CAE, 2021) — documentación y daily en remoto con US. Español nativo. Francés B1 — lectura, no negociación. El segundo bloque se puede defender. El primero se cae en el minuto tres de la entrevista en inglés.',
        },
        { type: 'h2', text: 'Dónde ponerlo (y cuándo subirlo al resumen)' },
        {
          type: 'p',
          text: 'Si el idioma es requisito de la oferta, que aparezca tres veces de forma natural: en el resumen (“CSM B2B, inglés C1”), en una viñeta de experiencia (“reuniones quincenales con el equipo de Dublín”) y en la sección Idiomas. El ATS a veces puntúa el encabezado y las skills; un B2 solo en un recuadro gráfico del margen se pierde. Guía de [palabras clave ATS](/blog/palabras-clave-cv-ats): el término tiene que estar en texto, no en un pictograma.',
        },
        {
          type: 'p',
          text: 'Si el idioma es un plus, basta la sección al final, después de habilidades. No robes espacio al logro. Un junior con inglés B2 y poco más puede poner Idiomas más arriba, justo bajo formación: es de lo poco que diferencia el PDF. Detalle de cuándo el documento va en inglés o en español: [CV en inglés o en español](/blog/cv-ingles-o-espanol).',
        },
        {
          type: 'ul',
          items: [
            'Encabezado: ciudad e idioma de trabajo solo si es un imán real (“Madrid · English C1”).',
            'Resumen: una mención si la oferta lo exige o si tu mercado es bilingüe.',
            'Experiencia: prueba de uso (clientes, tickets, documentación), no “ambiente internacional”.',
            'Sección Idiomas: lista corta, MCER, certificado si existe y no es de 2009 sin uso posterior.',
          ],
        },
        { type: 'h2', text: 'Certificados, caducidad y el “B2 de la ESO”' },
        {
          type: 'p',
          text: 'Un First, Advanced, IELTS, TOEFL, DELE, Goethe o DELF es una keyword útil. Pon entidad y año. IELTS y TOEFL “caducan” para universidades a los dos años; en empresa el papel sigue siendo una señal, pero el uso reciente pesa más. Si tu B2 es de selectividad y llevas diez años sin hablar, no lo inflés a C1. Baja el nivel o escribe “B2 (formación; uso ocasional)”. Honestidad aquí evita un screening brutal.',
        },
        {
          type: 'p',
          text: 'No listes Duolingo o un curso de 20 horas como si fuera un C1. Puedes meterlo en formación complementaria: “Inglés aplicado a negocios, 2024 (40 h)”. El ATS puede capturar “inglés”; el humano no te creerá un nivel de negociación. Si estás [adaptando el CV a una oferta](/blog/adaptar-cv-a-una-oferta), copia el nivel que pide el anuncio solo si lo tienes. “Inglés negociador” cuando eres B1 es un filtro inverso: te llaman y te descartan.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Las banderas no son un nivel MCER',
          lead: 'Si tus idiomas son iconos o “alto / medio”, el ATS no extrae B2 ni C1. Sube el PDF y regenera la sección en texto.',
          from: 'Banderas y “inglés alto”',
          to: 'Inglés B2 en texto',
          button: 'Comprobar mis idiomas',
        },
        { type: 'h2', text: 'Banderas, barras y el test del bloc de notas' },
        {
          type: 'p',
          text: 'Plantillas de Canva y Europass “bonito” ponen una bandera y cinco puntos de color. Tú ves “C1”. El parser ve un dibujo o un carácter raro. Abre el PDF, selecciona la fila de idiomas, pégala en el bloc de notas. Si no sale “Inglés B2”, ese archivo no lleva tu keyword a Workday ni a InfoJobs. Misma lógica que en [CV de Canva y ATS](/blog/cv-canva-y-ats): el diseño no es el documento que envías al portal.',
        },
        {
          type: 'p',
          text: 'En ClarCV subes el PDF o Word que ya tienes. El análisis señala claridad y fricción de estructura (no un porcentaje mágico contra un ATS concreto). Luego aplicas una plantilla A4 con secciones estándar, idiomas en texto. Empieza gratis (3 análisis y 2 descargas / 30 días, sin tarjeta). Pro son 7 €/mes si iteras a menudo.',
        },
        { type: 'h2', text: 'Varios idiomas y el que no debes inflar' },
        {
          type: 'p',
          text: 'Ordena por relevancia para el puesto, no por el que más te enorgullece. Oferta en Barcelona con catalán valorable: Catalán C1, Español nativo, Inglés B2. Oferta en inglés en remoto US: English C1 first. No pongas diez lenguas A1 de vacaciones; diluyen el B2 real. El catalán, euskera o gallego son keywords de mercado local: escríbelos con MCER, no con “nativo” si no lo son.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Puedo poner “bilingüe” en lugar de C2?',
              a: 'Mejor nativo + el otro idioma con MCER, o “bilingüe ES/EN” solo si de verdad trabajas y piensas en ambos. “Bilingüe” inflado se nota en la primera call.',
            },
            {
              q: '¿El ATS entiende B2 y “upper intermediate”?',
              a: 'En España prioriza B2/C1. Si la oferta está en inglés, puedes poner English B2 (upper-intermediate) la primera vez. No sustituyas el código MCER por jerga sola.',
            },
            {
              q: '¿Pongo el idioma en el que está escrito el CV?',
              a: 'Sí, en la sección Idiomas. Un CV en español con “English C1” es coherente. Un CV en inglés para una oferta en español, salvo que lo pidan, suele ser peor idea: ver la guía EN/ES.',
            },
            {
              q: '¿Y el idioma de programación?',
              a: 'No va en Idiomas. Python no es un MCER. Va en habilidades o en el stack de cada puesto.',
            },
            {
              q: '¿ClarCV reescribe mis niveles?',
              a: 'No inventa un C1. Conserva lo que está en el archivo y lo pasa a texto parseable. El nivel lo decides tú.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Languages on a CV: CEFR levels (A1–C2) and how to list them',
      description:
        'How to list languages on a résumé with CEFR levels in text (not flags). Which certificate to add, what not to inflate, and how ATS reads “English B2”.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'If you are searching how to list languages on a CV, the expensive mistake is not “forgetting French”. It is writing “fluent English”, three flags, or a C1 you cannot hold for five minutes on a call. In Spain and much of Europe, hiring teams expect the CEFR: A1 to C2. ATS tools look for those labels or the certificate name. A flag icon is not a keyword.',
        },
        {
          type: 'p',
          text: 'This guide covers where to place the section, how to map “native / bilingual / fluent” to A1–C2, what to do with a decade-old First, and when the language belongs in the summary as well as at the bottom. If you already have a PDF, you do not start from scratch: check that the level is selectable text. ClarCV (clarcv.com) fits here: upload the file, see whether parsing eats the flags, and regenerate a template with Languages in words.',
        },
        {
          type: 'p',
          text: 'You will not “hack” the European framework. You will be precise. An honest B2 with “client meetings in English” beats a decorative C1. In interview you will be asked to tell a project in that language. The chatbot will not join you. Neither will a certificate you do not have.',
        },
        { type: 'h2', text: 'Use CEFR (A1–C2), not adjectives' },
        {
          type: 'p',
          text: '“Intermediate English”, “fluent French”, “conversational German” mean different things to two recruiters. CEFR does not: A1/A2 (basic), B1/B2 (independent), C1/C2 (proficient). On a CV for Spain, write the language and the level: English B2. If you have a certificate, add it once: English B2 (First Certificate, 2022). You do not need to explain the framework; European hiring already knows it.',
        },
        {
          type: 'ul',
          items: [
            'A1–A2: travel, short phrases. Rarely deserves its own line unless the ad asks for “notions”.',
            'B1: routine work; not yet a hard negotiation.',
            'B2: the most requested bar in Spanish ads (“good English”). Meetings, email, docs.',
            'C1: reports, customers, presentations without a net. Do not claim it if you freeze on a screen.',
            'C2: near-native professional use. Reserve it for people who work in that language daily.',
            'Native: schooling or home language. “Native” is not an academy C2.',
          ],
        },
        {
          type: 'p',
          text: 'Before: English: fluent. French: intermediate. Flags. After: English C1 (CAE, 2021) — docs and dailies with a US team. Spanish native. French B1 — reading, not negotiation. The second block is defensible. The first dies in minute three of the English interview.',
        },
        { type: 'h2', text: 'Where to put it (and when to raise it into the summary)' },
        {
          type: 'p',
          text: 'If the language is a job requirement, let it appear three times naturally: in the summary (“B2B CSM, English C1”), in an experience bullet (“fortnightly calls with the Dublin team”), and in Languages. ATS tools sometimes score the header and skills; a B2 only in a graphic sidebar is lost. See [ATS keywords](/blog/palabras-clave-cv-ats): the term must be text, not a pictogram.',
        },
        {
          type: 'p',
          text: 'If the language is a nice-to-have, a short section at the end is enough. Do not steal space from proof. A junior with English B2 and little else can place Languages just under education: it is one of the few differentiators. When the whole file should be English vs Spanish: [CV in English or Spanish](/blog/cv-ingles-o-espanol).',
        },
        {
          type: 'ul',
          items: [
            'Header: city and working language only if it is a real magnet (“Madrid · English C1”).',
            'Summary: one mention if the ad requires it or your market is bilingual.',
            'Experience: proof of use (clients, tickets, documentation), not “international environment”.',
            'Languages section: short list, CEFR, certificate if it exists and is not a 2009 paper with no later use.',
          ],
        },
        { type: 'h2', text: 'Certificates, expiry, and the “high-school B2”' },
        {
          type: 'p',
          text: 'A First, Advanced, IELTS, TOEFL, DELE, Goethe, or DELF is a useful keyword. Add body and year. IELTS and TOEFL “expire” for universities after two years; at companies the paper still signals, but recent use weighs more. If your B2 is from secondary school and you have not spoken in a decade, do not inflate it to C1. Drop the level or write “B2 (coursework; occasional use)”. Honesty here avoids a brutal screening.',
        },
        {
          type: 'p',
          text: 'Do not list Duolingo or a 20-hour course as a C1. You can put it under extra training: “Business English, 2024 (40 h)”. ATS may catch “English”; a human will not believe negotiation level. If you are [tailoring the CV to a job](/blog/adaptar-cv-a-una-oferta), copy the level from the ad only if you have it. “Negotiating English” at B1 is a reverse filter: they call, then drop you.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Flags are not a CEFR level',
          lead: 'If your languages are icons or “fluent / intermediate”, ATS will not extract B2 or C1. Upload the PDF and regenerate the section as text.',
          from: 'Flags and “fluent”',
          to: 'English B2 in text',
          button: 'Check my languages',
        },
        { type: 'h2', text: 'Flags, bars, and the notepad test' },
        {
          type: 'p',
          text: 'Canva and “pretty” Europass templates put a flag and five coloured dots. You see “C1”. The parser sees a drawing or a odd character. Open the PDF, select the language row, paste into Notepad. If “English B2” does not appear, that file is not taking your keyword into Workday or a job board. Same logic as [Canva CV and ATS](/blog/cv-canva-y-ats): the design is not the file you send to the portal.',
        },
        {
          type: 'p',
          text: 'In ClarCV you upload the PDF or Word you already have. Analysis flags clarity and structure friction (not a magic score against a named ATS). Then you apply an A4 template with standard headings and languages in text. Start free (3 analyses and 2 downloads / 30 days, no card). Pro is €7/month if you iterate often.',
        },
        { type: 'h2', text: 'Several languages and the one you must not inflate' },
        {
          type: 'p',
          text: 'Order by relevance to the role, not by pride. Barcelona ad that values Catalan: Catalan C1, Spanish native, English B2. English-language remote US role: English C1 first. Do not list ten holiday A1s; they dilute a real B2. Catalan, Basque, or Galician are local-market keywords: write them with CEFR, not “native” if they are not.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Can I write “bilingual” instead of C2?',
              a: 'Better: native + the other language with CEFR, or “bilingual ES/EN” only if you actually work and think in both. Inflated “bilingual” shows on the first call.',
            },
            {
              q: 'Does ATS understand both B2 and “upper intermediate”?',
              a: 'In Spain it prefers B2/C1. If the ad is in English, you can write English B2 (upper-intermediate) once. Do not replace the CEFR code with jargon alone.',
            },
            {
              q: 'Should I list the language the CV is written in?',
              a: 'Yes, in Languages. A Spanish CV with “English C1” is coherent. An English CV for a Spanish-language ad is usually a worse idea unless they asked — see the EN/ES guide.',
            },
            {
              q: 'What about programming languages?',
              a: 'They do not belong in Languages. Python is not CEFR. Put it in skills or in each role’s stack.',
            },
            {
              q: 'Does ClarCV rewrite my levels?',
              a: 'It does not invent a C1. It keeps what is in the file and turns it into parseable text. You decide the level.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'enviar-cv-por-email',
    date: '2026-08-22',
    category: 'career',
    related: ['checklist-cv-antes-de-enviar', 'candidatura-espontanea-cv', 'seguimiento-despues-de-enviar-cv'],
    es: {
      title: 'Cómo enviar el CV por email: asunto, cuerpo y adjunto',
      description:
        'Asunto que se abre, cuerpo de tres párrafos y PDF que el ATS puede leer. Ejemplos reales y errores que tiran el correo. ClarCV.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Si buscas cómo enviar un currículum por email, el fallo no suele ser “no tener experiencia”. Es un asunto “CV”, un cuerpo de diez líneas vacías y un PDF de Canva que pesa 8 MB o no se puede seleccionar. Quien filtra recibe cientos de mensajes. Los que sobreviven tienen referencia de la vacante, un adjunto con nombre limpio y un archivo que se abre en el móvil.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre el asunto (con ejemplos que sí se abren), el cuerpo como mini carta, el nombre del archivo, PDF vs Word, y qué cambia si es candidatura espontánea o si el anuncio pide portal. El [checklist antes de enviar](/blog/checklist-cv-antes-de-enviar) es el hermano de esta página: aquí el canal es el correo. ClarCV entra cuando el adjunto aún es un diseño frágil: lo subes, lo regeneras parseable y entonces sí pulsas enviar.',
        },
        {
          type: 'p',
          text: 'No vas a “hackear” Gmail del reclutador. Vas a quitar fricción. Un email impecable con un PDF-imagen sigue perdiendo el ATS del siguiente paso. Un PDF limpio con un asunto genérico a veces ni se abre. Hacen falta las dos mitades.',
        },
        { type: 'h2', text: 'Asunto del correo: 20 patrones que sí se abren' },
        {
          type: 'p',
          text: 'El asunto es el filtro cero. “CV adjunto”, “Busco trabajo” o el vacío se mezclan con spam. Incluye puesto, tu nombre y, si existe, la referencia. Máximo ~60 caracteres para que no se corte en el móvil.',
        },
        {
          type: 'ul',
          items: [
            'Customer Success Manager — Ana López',
            'Ref. 1847 / Analista de datos — Ana López',
            'Candidatura: camarero/a temporada — Ana López (disponibilidad inmediata)',
            'Prácticas marketing 2026 — Ana López, UB',
            'Candidatura espontánea — Product designer, 6 años e-commerce',
            'Oferta LinkedIn 22 ago — CSM B2B — Ana López',
            'Sustitución baja maternal — Administrativo/a — Ana López',
            'Recomendación de Marta Ruiz — Dev backend — Ana López',
            'Vacante InfoJobs “Account Executive Madrid” — Ana López',
            'Ingeniero/a de procesos — Ana López — reloc. Sevilla',
          ],
        },
        {
          type: 'p',
          text: 'Evita: “URGENTE”, emojis, todo mayúsculas, “lea por favor”, y el nombre de tu archivo interno (CV_final_v8). Si la empresa pidió un código, ese código va primero: es cómo buscan en el buzón. Más contexto de portales frente a email directo: [CV para InfoJobs, LinkedIn y portales](/blog/cv-para-portales-empleo).',
        },
        { type: 'h2', text: 'Cuerpo: tres párrafos, no una novela' },
        {
          type: 'p',
          text: 'El email no es la carta de presentación de dos páginas. Es una tapadera para que abran el PDF. Párrafo 1: quién eres y a qué puesto (o por qué escribes en frío). Párrafo 2: una prueba (cifra, herramienta, años en el sector). Párrafo 3: disponibilidad, ciudad/remoto y que el CV va adjunto. Cierra con nombre, teléfono y LinkedIn en texto.',
        },
        {
          type: 'p',
          text: 'Antes: “Me dirijo a usted para solicitar… adjunto mi CV para su consideración… quedo a la espera.” Cero señal. Después: “Solicito la vacante de CSM (ref. 1847). Llevé 40 cuentas en HubSpot y bajé la primera respuesta de 12 a 5 horas. Adjunto PDF. Estoy en Madrid, híbrido, incorporación en 15 días.” El segundo email se puede reenviar internamente sin vergüenza.',
        },
        {
          type: 'ul',
          items: [
            'Mismo idioma que el anuncio. No mezcles un cuerpo en español con un CV solo en inglés sin motivo.',
            'Sin HTML recargado ni firma con diez logos: algunos clientes recortan o marcan spam.',
            'No pongas el CV pegado como imagen en el cuerpo. Adjunto + texto.',
            'Relee en voz alta. Una errata en el nombre de la empresa mata más que un diseño soso.',
          ],
        },
        { type: 'h2', text: 'El adjunto: nombre, peso y formato' },
        {
          type: 'p',
          text: 'Nombre: Ana_Lopez_CSM.pdf. No “curriculum”, no “sin título”, no caracteres raros. PDF con texto seleccionable para la mayoría de buzones en España. Word solo si el anuncio lo pide. Detalle: [CV en PDF o Word](/blog/cv-pdf-vs-word). Peso: apunta a menos de 2 MB. Fotos a 300 dpi de un estudio hunden el correo y algunos portales.',
        },
        {
          type: 'p',
          text: 'Haz la prueba antes de enviar: ábrelo en el teléfono, selecciona el email del encabezado, pégalo en notas. Si no sale texto, no lo envíes a un proceso con ATS. El reclutador humano a veces perdona; el parser del siguiente software no.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El asunto abre el correo. El PDF pasa el filtro',
          lead: 'Si el adjunto es Canva, escaneo o un archivo de 8 MB, el email perfecto no basta. Regenera un PDF ligero y seleccionable y entonces envía.',
          from: 'Email listo',
          to: 'PDF que se puede parsear',
          button: 'Revisar el adjunto',
        },
        { type: 'h2', text: 'Candidatura espontánea vs vacante con referencia' },
        {
          type: 'p',
          text: 'Con referencia: asunto con código, cuerpo corto, un logro alineado al anuncio. Espontánea: di el equipo o el tipo de rol, por qué esa empresa (un hecho, no “líder del sector”) y que aceptas que no haya vacante abierta. Adjunta el CV; no pidas que “entren a un Drive”. Un enlace que caduca o pide permiso es un no silencioso.',
        },
        {
          type: 'p',
          text: 'Si ya aplicaste por InfoJobs Easy Apply, no reenvíes el mismo PDF por email al azar el mismo día: parece spam. Sí tiene sentido un email al hiring manager si tienes un contacto real o una nota de “hablamos en la feria”. El archivo debe ser el mismo que el del portal: fechas y títulos iguales. Contradicción CV/email/LinkedIn = descarte.',
        },
        { type: 'h2', text: 'Cuándo arreglar el archivo antes de pulsar enviar' },
        {
          type: 'p',
          text: 'Si tu PDF viene de Canva, de un escaneo o de un Europass de tres páginas, el email solo maquilla. Sube el archivo a ClarCV, mira claridad y estructura, aplica una plantilla ATS A4 y descarga. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Luego pegas el asunto de esta guía y envías. El orden importa: primero el documento, después el sobre.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Carta de presentación adjunta o solo el cuerpo?',
              a: 'Si no la piden, el cuerpo del email basta. Si la piden, PDF aparte con nombre Ana_Lopez_carta.pdf, misma historia que el CV, sin copiar el email entero.',
            },
            {
              q: '¿A qué hora enviar?',
              a: 'Laborable, mañana. Evita domingo noche y viernes a última hora. No es magia; es no caer bajo 200 mensajes del lunes.',
            },
            {
              q: '¿Copio a varios destinatarios?',
              a: 'No en CCO masivo a diez empresas. Un correo, un proceso. El CCO se nota cuando alguien responde a todos.',
            },
            {
              q: '¿Puedo usar un alias tipo cv@?',
              a: 'Mejor nombre.apellido@gmail o similar, sin apodos. La dirección es parte del asunto visual.',
            },
            {
              q: '¿ClarCV envía el email por mí?',
              a: 'No. Prepara el PDF. El envío lo haces tú, con el asunto y el cuerpo de esta guía.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'How to email your CV: subject, body, and attachment',
      description:
        'A subject people open, a three-paragraph body, and a PDF ATS can read. Real examples and mistakes that kill the message. ClarCV.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'If you are searching how to email a CV, the failure is rarely “no experience”. It is a subject line that says “CV”, a hollow ten-line body, and a Canva PDF that weighs 8 MB or cannot be selected. Screeners get hundreds of messages. Survivors have a vacancy reference, a clean filename, and a file that opens on a phone.',
        },
        {
          type: 'p',
          text: 'This guide covers the subject (with examples that get opened), the body as a mini cover note, the filename, PDF vs Word, and what changes for a speculative application vs a portal. The [pre-send checklist](/blog/checklist-cv-antes-de-enviar) is the sibling page: here the channel is email. ClarCV belongs when the attachment is still a fragile design: upload, regenerate something parseable, then hit send.',
        },
        {
          type: 'p',
          text: 'You will not “hack” the recruiter’s Gmail. You will remove friction. A perfect email with an image-PDF still loses the next ATS. A clean PDF with a generic subject sometimes never opens. You need both halves.',
        },
        { type: 'h2', text: 'Email subject: 20 patterns that get opened' },
        {
          type: 'p',
          text: 'The subject is filter zero. “CV attached”, “Looking for work”, or a blank subject mix with spam. Include the role, your name, and a reference if it exists. Stay around 60 characters so mobile does not crop it.',
        },
        {
          type: 'ul',
          items: [
            'Customer Success Manager — Ana López',
            'Ref. 1847 / Data analyst — Ana López',
            'Application: seasonal waiter — Ana López (immediate start)',
            'Marketing internship 2026 — Ana López, UB',
            'Speculative application — Product designer, 6 years e-commerce',
            'LinkedIn posting 22 Aug — B2B CSM — Ana López',
            'Maternity cover — Admin — Ana López',
            'Referral from Marta Ruiz — Backend dev — Ana López',
            'InfoJobs vacancy “Account Executive Madrid” — Ana López',
            'Process engineer — Ana López — reloc. Seville',
          ],
        },
        {
          type: 'p',
          text: 'Avoid: “URGENT”, emoji, all caps, “please read”, and your internal filename (CV_final_v8). If they asked for a code, put that code first: that is how they search the inbox. More on boards vs direct email: [CVs for InfoJobs, LinkedIn, and job boards](/blog/cv-para-portales-empleo).',
        },
        { type: 'h2', text: 'Body: three paragraphs, not a novel' },
        {
          type: 'p',
          text: 'The email is not a two-page cover letter. It is a lid so they open the PDF. Paragraph 1: who you are and which role (or why you are writing cold). Paragraph 2: one proof (a figure, a tool, years in the sector). Paragraph 3: availability, city/remote, and that the CV is attached. Close with name, phone, and LinkedIn as text.',
        },
        {
          type: 'p',
          text: 'Before: “I am writing to apply… please find my CV… I look forward to hearing from you.” Zero signal. After: “I am applying for CSM (ref. 1847). I ran 40 accounts in HubSpot and cut first response from 12 hours to 5. PDF attached. I am in Madrid, hybrid, 15 days’ notice.” The second email can be forwarded internally without embarrassment.',
        },
        {
          type: 'ul',
          items: [
            'Same language as the ad. Do not mix a Spanish body with an English-only CV without a reason.',
            'No heavy HTML or a signature with ten logos: some clients clip or flag spam.',
            'Do not paste the CV as an image in the body. Attachment + text.',
            'Read it aloud. A typo in the company name kills more than a plain design.',
          ],
        },
        { type: 'h2', text: 'The attachment: name, size, and format' },
        {
          type: 'p',
          text: 'Filename: Ana_Lopez_CSM.pdf. Not “curriculum”, not “untitled”, not odd characters. Selectable-text PDF for most Spanish inboxes. Word only if the ad asks. Detail: [PDF or Word CV](/blog/cv-pdf-vs-word). Size: aim under 2 MB. Studio photos at 300 dpi sink the email and some portals.',
        },
        {
          type: 'p',
          text: 'Test before sending: open it on your phone, select the header email, paste into Notes. If no text appears, do not send it into an ATS process. A human recruiter sometimes forgives; the next parser does not.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The subject opens the email. The PDF passes the filter',
          lead: 'If the attachment is Canva, a scan, or an 8 MB file, a perfect email is not enough. Regenerate a light, selectable PDF, then send.',
          from: 'Email ready',
          to: 'A parseable PDF',
          button: 'Check the attachment',
        },
        { type: 'h2', text: 'Speculative email vs a referenced vacancy' },
        {
          type: 'p',
          text: 'With a reference: code in the subject, short body, one achievement aligned to the ad. Speculative: name the team or role type, why that company (one fact, not “industry leader”), and that you accept there may be no opening. Attach the CV; do not ask them to “open a Drive”. An expiring or permissioned link is a silent no.',
        },
        {
          type: 'p',
          text: 'If you already applied via Easy Apply, do not blast the same PDF by email the same day: it looks like spam. A note to the hiring manager makes sense if you have a real contact or “we spoke at the fair”. The file must match the portal: same dates and titles. CV/email/LinkedIn contradiction = reject.',
        },
        { type: 'h2', text: 'When to fix the file before you hit send' },
        {
          type: 'p',
          text: 'If your PDF is Canva, a scan, or a three-page Europass, the email is only makeup. Upload to ClarCV, check clarity and structure, apply an A4 ATS template, download. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. Then paste a subject from this guide and send. Order matters: document first, envelope second.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Cover letter attached or body only?',
              a: 'If they did not ask, the email body is enough. If they asked, a separate PDF named Ana_Lopez_cover.pdf, same story as the CV, not a paste of the whole email.',
            },
            {
              q: 'What time should I send?',
              a: 'Weekday morning. Avoid Sunday night and late Friday. It is not magic; it is not landing under 200 Monday messages.',
            },
            {
              q: 'Should I CC several people?',
              a: 'Not a mass BCC to ten companies. One email, one process. BCC shows when someone replies-all.',
            },
            {
              q: 'Can I use an alias like cv@?',
              a: 'Prefer name.lastname@gmail or similar, no nicknames. The address is part of the visual subject.',
            },
            {
              q: 'Does ClarCV send the email for me?',
              a: 'No. It prepares the PDF. You send it, with the subject and body from this guide.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'que-no-poner-en-el-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['foto-en-el-cv', 'cv-firmado', 'cv-ingles-o-espanol'],
    es: {
      title: 'Qué no poner en un CV en 2026 (y qué sí espera RR. HH.)',
      description:
        'DNI, fecha de nacimiento, estado civil y salario: cuándo sobran. Qué dejar y cómo limpiar el PDF que ya tienes. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Qué no poner en un CV en 2026 no es una lista moral. Es una lista de fricción: datos que no ayudan a pasar el ATS, que ocupan la primera pantalla o que en algunos países son una bandera roja legal. DNI, fecha de nacimiento, estado civil, número de hijos, salario actual y “referencias se facilitarán” siguen apareciendo en PDFs copiados de 2012. En España muchos aún se ponen. En un proceso UK o US, varios de esos campos descartan.',
        },
        {
          type: 'p',
          text: 'Esta guía va campo a campo: qué quitar, qué dejar según el país, y cómo no romper el parseo al limpiar. Si tu archivo ya está hecho, no lo tires: recorta cabecera y márgenes. El detalle de foto está en [foto en el CV](/blog/foto-en-el-cv); aquí el resto de datos personales. ClarCV sirve para regenerar el mismo contenido sin el ruido, en una plantilla que el filtro lee.',
        },
        {
          type: 'p',
          text: 'No vas a “anonimizarte” hasta volverte invisible. Vas a dejar lo que un reclutador necesita para contactarte y evaluar el trabajo. Todo lo demás es folklore de academia o de plantillas de Word del instituto.',
        },
        { type: 'h2', text: 'DNI, NIE y número de la Seguridad Social' },
        {
          type: 'p',
          text: 'No pongas el DNI ni el NIE en el CV que envías a un portal o a un email masivo. Es un dato sensible, no una keyword de empleo, y si el PDF circula has expuesto identidad. La empresa lo pedirá en la fase de contrato o de verificación, con un canal más cerrado. Excepción estrecha: algunos procesos de empleo público o de seguridad piden identificación en un formulario aparte, no como adorno en la cabecera del PDF.',
        },
        {
          type: 'p',
          text: 'El número de la Seguridad Social, la cuenta bancaria o el pasaporte completo no van al currículum. Nunca. Si una plantilla de “hoja de vida” latinoamericana incluye cédula por costumbre, quítala para multinacionales y para ATS europeos. Puedes dejarla en un formato oficial de empleo público local si ese formato lo exige — ese no es el PDF de InfoJobs.',
        },
        { type: 'h2', text: 'Fecha de nacimiento, edad y estado civil' },
        {
          type: 'p',
          text: 'En España todavía se ve la fecha de nacimiento. No es ilegal ponerla, pero no suma y alimenta sesgo. El ATS no necesita tu edad para extraer Python. Si tienes más de 45, la fecha en la primera línea trabaja en tu contra antes de leer los logros. Quítala. Si quieren edad, la inferirán de las fechas de estudio; no se la sirvas en bandeja. Guía hermana cuando el problema es el recorte de trayectoria: [CV si tienes más de 45 o 50](/blog/cv-mayores-de-50).',
        },
        {
          type: 'p',
          text: 'Estado civil, hijos, “disponible para viajar porque no tengo cargas”: fuera. Es información protegida y suena a otra década. Disponibilidad de viaje o de turnos se dice como hecho laboral (“turnos rotativos 2022–2024”), no como vida privada. Nacionalidad: solo si es requisito del puesto (permiso de trabajo, empleo público). “Español” en un CV para Madrid rara vez hace falta; “permiso de trabajo UE” sí puede ser una línea útil si no es obvio.',
        },
        {
          type: 'ul',
          items: [
            'Quitar: DNI/NIE, fecha de nacimiento, estado civil, hijos, religión, partido, foto carnet borrosa.',
            'Quitar: salario actual, pretensión económica en el PDF (eso va en el formulario o en la entrevista).',
            'Quitar: “referencias se facilitarán a petición” — no aporta; el espacio vale más.',
            'Dejar: email, teléfono, ciudad (no calle y número), LinkedIn, permiso de trabajar si aplica.',
            'Según país: foto en España a menudo se espera; en UK/US, no. Ver guía de foto.',
          ],
        },
        { type: 'h2', text: 'Dirección, salario y el objetivo de los 90' },
        {
          type: 'p',
          text: 'Calle, portal y código postal: no. Ciudad y, si aporta, “dispuesto a relocarse a Bilbao”. Una dirección exacta es un riesgo de privacidad y no se busca en el ATS. Salario: no lo pongas en el CV. Si el anuncio pide pretensión, respóndela en el campo del portal o en el email, no como una viñeta eterna que te ancla a la baja.',
        },
        {
          type: 'p',
          text: 'El “objetivo profesional” genérico (“deseoso de formar parte de una empresa líder…”) sobra. Sustitúyelo por un [resumen profesional](/blog/resumen-profesional-cv) de 2–4 líneas con rol, sector y una prueba. El objetivo de instituto ocupa sitio y no contiene keywords de la oferta.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'DNI y fecha de nacimiento no pasan ningún filtro útil',
          lead: 'Si la cabecera de tu PDF parece un formulario de 2010, recórtala. Conserva hechos y contacto; regenera el archivo sin datos que solo restan.',
          from: 'DNI, edad, estado civil',
          to: 'Contacto y logros',
          button: 'Limpiar mi CV',
        },
        { type: 'h2', text: 'Lo que sí espera RR. HH. en 2026' },
        {
          type: 'p',
          text: 'Un humano en España, en diez segundos: nombre, puesto objetivo implícito, ciudad, si hay hueco raro, y dos logros con cifra. Un ATS: email en texto, fechas, encabezados Experiencia / Formación / Habilidades, keywords de la oferta. Por eso quitar ruido no es “minimalismo de moda”: es dejar que esas señales existan. Idioma del documento = idioma del anuncio. Detalle EN/ES: [CV en inglés o en español](/blog/cv-ingles-o-espanol).',
        },
        {
          type: 'p',
          text: 'Hobbies irrelevantes, barras de Excel al 80% e iconos de teléfono: también son “qué no poner” de formato. Van en [iconos en el CV](/blog/iconos-en-el-cv) e [intereses en el CV](/blog/intereses-en-el-cv). Aquí el criterio es el mismo: si no se puede copiar o no se puede defender, fuera.',
        },
        { type: 'h2', text: 'Cómo limpiar el archivo que ya tienes' },
        {
          type: 'p',
          text: 'No rediseñes en Canva celda a celda. Extrae el contenido, borra DNI y fecha, deja ciudad y teléfono en texto, y colócalo en una columna. En ClarCV subes el PDF o Word, el análisis señala estructura y claridad, y regeneras plantilla ATS. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. El [checklist](/blog/checklist-cv-antes-de-enviar) te dice cuándo ya puedes pulsar enviar.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿En España debo poner la fecha de nacimiento sí o sí?',
              a: 'No. Es costumbre, no requisito. Cada vez más procesos corporativos prefieren no verla. Quitar la fecha no te hace “menos español”; te hace menos sesgable.',
            },
            {
              q: '¿Y si el formulario del portal pide DNI?',
              a: 'Rellena el formulario. Eso no justifica imprimir el DNI en el PDF que se reenvía a diez personas.',
            },
            {
              q: '¿Puedo poner el salario si me lo piden en el anuncio?',
              a: 'En el campo del anuncio o en el email, no como línea fija del CV para todas las ofertas. El PDF viaja.',
            },
            {
              q: '¿El estado civil ayuda en hostelería o atención al público?',
              a: 'No. Disponibilidad de turnos sí, como hecho de experiencia. El estado civil no.',
            },
            {
              q: '¿ClarCV borra el DNI solo?',
              a: 'Revisa el análisis y el editor. Tú confirmas qué datos personales quedan. La plantilla no necesita DNI para parsear.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'What not to put on a CV in 2026 (and what HR still needs)',
      description:
        'National ID, date of birth, marital status, and salary: when they hurt. What to keep and how to clean the PDF you already have. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'What not to put on a CV in 2026 is not a moral list. It is a friction list: data that does not help ATS, that burns the first screen, or that is a legal red flag in some countries. National ID, date of birth, marital status, number of children, current salary, and “references available on request” still show up on PDFs copied from 2012. In Spain many people still add them. In a UK or US process, several of those fields reject you.',
        },
        {
          type: 'p',
          text: 'This guide goes field by field: what to remove, what to keep by country, and how not to break parsing when you clean. If the file already exists, do not throw it away: trim the header and the margins. Photo detail lives in [photo on the CV](/blog/foto-en-el-cv); this page is the rest of the personal data. ClarCV is how you regenerate the same content without the noise, in a template the filter can read.',
        },
        {
          type: 'p',
          text: 'You will not anonymise yourself into invisibility. You will leave what a recruiter needs to contact you and judge the work. Everything else is school folklore or a Word template from sixth form.',
        },
        { type: 'h2', text: 'National ID, NIE, and social-security numbers' },
        {
          type: 'p',
          text: 'Do not put Spanish DNI or NIE on the CV you send to a board or a mass email. It is sensitive, not a hiring keyword, and if the PDF circulates you have exposed identity. The company will ask at contract or verification, in a tighter channel. Narrow exception: some public-sector or security processes want ID in a separate form, not as decoration in the PDF header.',
        },
        {
          type: 'p',
          text: 'Social-security numbers, bank accounts, or a full passport do not belong on a CV. Ever. If a Latin American “hoja de vida” template includes a cédula by habit, remove it for multinationals and European ATS. You can leave it on an official local public-employment form if that form requires it — that is not the InfoJobs PDF.',
        },
        { type: 'h2', text: 'Date of birth, age, and marital status' },
        {
          type: 'p',
          text: 'In Spain you still see date of birth. It is not illegal to add it, but it does not help and it feeds bias. ATS does not need your age to extract Python. If you are over 45, the date on line one works against you before anyone reads achievements. Remove it. If they want age, they will infer it from education dates; do not serve it. Sister guide when the issue is cutting a long career: [CV if you are over 45 or 50](/blog/cv-mayores-de-50).',
        },
        {
          type: 'p',
          text: 'Marital status, children, “available to travel because I have no dependents”: out. It is protected information and it sounds like another decade. Travel or shift availability is a work fact (“rotating shifts 2022–2024”), not private life. Nationality: only if the role requires it (work permit, public employment). “Spanish” on a Madrid CV is rarely needed; “EU work authorisation” can be a useful line if it is not obvious.',
        },
        {
          type: 'ul',
          items: [
            'Remove: national ID, date of birth, marital status, children, religion, party, a blurry ID photo.',
            'Remove: current salary and salary expectations on the PDF (that belongs in a form or interview).',
            'Remove: “references available on request” — it adds nothing; the space is worth more.',
            'Keep: email, phone, city (not street number), LinkedIn, work authorisation if it applies.',
            'By country: a photo is often expected in Spain; in the UK/US, no. See the photo guide.',
          ],
        },
        { type: 'h2', text: 'Address, salary, and the 1990s objective' },
        {
          type: 'p',
          text: 'Street, building, and postcode: no. City and, if it helps, “willing to relocate to Bilbao”. A full address is a privacy risk and is not what ATS searches. Salary: do not put it on the CV. If the ad asks for expectations, answer in the portal field or the email, not as a permanent bullet that anchors you low.',
        },
        {
          type: 'p',
          text: 'The generic “career objective” (“eager to join a leading company…”) is surplus. Replace it with a [professional summary](/blog/resumen-profesional-cv) of 2–4 lines: role, sector, one proof. The school objective eats space and holds no keywords from the ad.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'National ID and date of birth do not pass any useful filter',
          lead: 'If your PDF header looks like a 2010 form, trim it. Keep facts and contact details; regenerate the file without data that only subtracts.',
          from: 'ID, age, marital status',
          to: 'Contact and proof',
          button: 'Clean my CV',
        },
        { type: 'h2', text: 'What HR still expects in 2026' },
        {
          type: 'p',
          text: 'A human in Spain, in ten seconds: name, implied target role, city, whether there is a weird gap, and two achievements with numbers. An ATS: email as text, dates, Experience / Education / Skills headings, keywords from the ad. That is why cutting noise is not “fashionable minimalism”: it lets those signals exist. Document language = ad language. EN/ES detail: [CV in English or Spanish](/blog/cv-ingles-o-espanol).',
        },
        {
          type: 'p',
          text: 'Irrelevant hobbies, Excel skill bars at 80%, and phone icons are also “what not to put”, as format. They live in [icons on the CV](/blog/iconos-en-el-cv) and [interests on the CV](/blog/intereses-en-el-cv). The test is the same: if it cannot be copied or cannot be defended, cut it.',
        },
        { type: 'h2', text: 'How to clean the file you already have' },
        {
          type: 'p',
          text: 'Do not redesign in Canva cell by cell. Extract the content, delete ID and date of birth, leave city and phone as text, and put it in one column. In ClarCV you upload the PDF or Word, analysis flags structure and clarity, and you regenerate an ATS template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. The [checklist](/blog/checklist-cv-antes-de-enviar) tells you when you can hit send.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Must I put date of birth on a Spanish CV?',
              a: 'No. It is habit, not a requirement. More corporate processes prefer not to see it. Removing the date does not make you “less Spanish”; it makes you less easy to bias.',
            },
            {
              q: 'What if the portal form asks for national ID?',
              a: 'Fill the form. That does not justify printing the ID on a PDF that gets forwarded to ten people.',
            },
            {
              q: 'Can I put salary if the ad asks?',
              a: 'In the ad’s field or in the email, not as a fixed line on the CV for every vacancy. The PDF travels.',
            },
            {
              q: 'Does marital status help in hospitality or retail?',
              a: 'No. Shift availability does, as an experience fact. Marital status does not.',
            },
            {
              q: 'Does ClarCV delete the ID on its own?',
              a: 'Check the analysis and the editor. You confirm which personal data stay. The template does not need an ID number to parse.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'intereses-en-el-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['skills-blandas-y-duras', 'resumen-profesional-cv', 'que-no-poner-en-el-cv'],
    es: {
      title: 'Intereses y hobbies en el CV: qué poner y qué quitar',
      description:
        'Datos de interés, hobbies e intereses en el currículum: cuáles suman al puesto, cuáles restan espacio y cuándo eliminar la sección entera.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Los intereses en el CV no consiguen la entrevista. Un hobby mal puesto sí puede perderla: “viajar, cine y música” ocupa la última quinta parte de una página que podría llevar un logro. La gente busca “hobbies CV” porque las plantillas dejan un hueco y da vergüenza dejarlo vacío. El criterio no es rellenar. Es señal. Si el interés no explica cómo trabajas o el entorno al que aplicas, sobra.',
        },
        {
          type: 'p',
          text: 'Esta guía separa intereses que funcionan (deporte de equipo en un rol de operaciones, open source en un CV de desarrollo, voluntariado medible) de los que son ruido. También cuándo borrar la sección: senior con dos páginas justas, o junior cuyo “interés” es Instagram. Relación con [hard y soft skills](/blog/skills-blandas-y-duras): un hobby no sustituye una competencia demostrada en un puesto. ClarCV ayuda cuando la sección está hinchada o en iconos: subes el PDF y recortas sin rehacer el diseño a mano.',
        },
        {
          type: 'p',
          text: 'No vas a inventar un hobby de trekking para parecer “resiliente”. Vas a dejar dos o tres líneas defendibles o ninguna. En la comida de equipo te preguntarán. “Lectura” no aguanta esa pregunta.',
        },
        { type: 'h2', text: 'Cuándo la sección suma' },
        {
          type: 'p',
          text: 'Suma si el interés es específico, verificable y cercano al puesto o a la cultura que piden. “Federado en club de running, 10 km sub-45” dice disciplina y dato. “Organizo un grupo de lectura de product management (20 personas, Meetup)” es liderazgo informal. “Contribuciones a un repo Django (enlace)” es portfolio. El reclutador puede googlear o preguntar. Eso vale.',
        },
        {
          type: 'ul',
          items: [
            'Deporte de equipo o federado, con un hecho (liga, dorsal, rol de capitán).',
            'Voluntariado con función y frecuencia, no “colaboro con ONG”.',
            'Proyectos públicos: blog, GitHub, newsletter, podcast con episodios reales.',
            'Afición que es keyword del sector (fotografía en un estudio, vela en un astillero, ajedrez en un rol analítico — con torneo o club, no el mito).',
            'Idiomas de ocio solo si no están ya en la sección Idiomas.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: Viajar, cine, música, internet, deporte. Después: Trail running (medias maratones 2023–2025) y radio municipal (técnico de sonido, 1 programa/semana). El primero es un sticker. El segundo es una historia de diez segundos.',
        },
        { type: 'h2', text: 'Cuáles sobran (aunque sean verdad)' },
        {
          type: 'p',
          text: 'Viajar, cine, series, música, “estar con amigos”, redes sociales, “internet”, cocina genérica, fútbol como espectador. No son falsos; son no información. También sobran intereses políticos o religiosos salvo roles donde son el trabajo. Cuidado con el humor ácido o los hobbies de riesgo si el sector es conservador: no es censura, es que el PDF viaja a personas que no te conocen.',
        },
        {
          type: 'p',
          text: 'Si eres junior y la página queda corta, no rellenes con hobbies. Añade un proyecto, un TFG en dos líneas, una práctica. Ver [CV para prácticas](/blog/cv-para-practicas) y [CV sin experiencia](/blog/cv-sin-experiencia). El hueco vacío es mejor que cinco clichés. Relación con [qué no poner](/blog/que-no-poner-en-el-cv): los intereses genéricos son ruido del mismo tipo que el estado civil.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Si los hobbies pesan más que los logros, el archivo está al revés',
          lead: 'Sube el CV que ya tienes. Recorta “viajar y cine”, deja dos intereses defendibles o ninguno, y regenera una página que se pueda enviar.',
          from: 'Viajar, cine, música',
          to: 'Dos líneas que se pueden preguntar',
          button: 'Recortar intereses',
        },
        { type: 'h2', text: 'Cómo escribirlos (sin barras ni emojis)' },
        {
          type: 'p',
          text: 'Texto plano, al final, máximo tres ítems. Nombre + hecho. No uses barras de “pasión 90%”. No uses iconos de bicicleta: el ATS no indexa el pictograma. No mezcles intereses con skills (“trabajo en equipo” no es un hobby). Si el interés es un proyecto, un enlace corto en texto (github.com/ana) es mejor que un QR decorativo que no se parsea.',
        },
        {
          type: 'p',
          text: 'El [resumen profesional](/blog/resumen-profesional-cv) no es el sitio de los hobbies. “Apasionada del running” en las tres primeras líneas roba el puesto y las keywords. El resumen es rol + prueba. El running, si acaso, al pie.',
        },
        { type: 'h2', text: 'Cuándo borrar la sección entera' },
        {
          type: 'p',
          text: 'Bórrala si con ella pasas de una página (junior/mid) o de dos (senior denso) y estás cortando logros. Bórrala en un CV académico o de consultora MBB si el espacio es oro. Déjala en pymes, hostelería, deporte, educación no formal, o cuando el proceso es muy humano (email directo a un estudio). El canal manda más que el consejo genérico de “humanizar el CV”.',
        },
        {
          type: 'p',
          text: 'En ClarCV subes el PDF, ves si la sección está hinchada o en iconos, y regeneras plantilla ATS. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Tú decides si los intereses se quedan; la herramienta no inventa un trekking.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Los intereses ayudan con el ATS?',
              a: 'Casi nunca. El ATS no busca “senderismo”. Ayudan a un humano si son específicos. No los optimices como keywords falsas.',
            },
            {
              q: '¿Pongo gaming si aplico a un estudio de videojuegos?',
              a: 'Sí, con hecho: título, rol en clan, mod, game jam. “Me gustan los videojuegos” no.',
            },
            {
              q: '¿Y si mi hobby es el trabajo (fotografía, código)?',
              a: 'Mejor como proyecto o experiencia. El pie de intereses es para lo que no encaja arriba.',
            },
            {
              q: '¿Tres aficiones o una?',
              a: 'Una excelente vale más que tres flojas. Máximo tres.',
            },
            {
              q: '¿ClarCV borra los hobbies solo?',
              a: 'Tú recortas. El análisis puede señalar densidad y estructura; la decisión es tuya.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Interests and hobbies on a CV: what to keep and what to cut',
      description:
        'Personal interests on a résumé: which hobbies add value for the role, which waste space, and when to delete the whole section.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Interests on a CV do not win the interview. A bad hobby can lose it: “travel, film, and music” eats the last fifth of a page that could hold an achievement. People search “hobbies CV” because templates leave a hole and leaving it empty feels rude. The test is not filling. It is signal. If the interest does not explain how you work or the environment you are applying to, cut it.',
        },
        {
          type: 'p',
          text: 'This guide separates interests that work (team sport in an operations role, open source on a dev CV, measurable volunteering) from noise. Also when to delete the section: a senior on a tight two pages, or a junior whose “interest” is Instagram. Related to [hard and soft skills](/blog/skills-blandas-y-duras): a hobby does not replace a skill proved in a job. ClarCV helps when the section is bloated or in icons: upload the PDF and trim without rebuilding the layout by hand.',
        },
        {
          type: 'p',
          text: 'You will not invent a trekking hobby to look “resilient”. You will leave two or three defensible lines, or none. At lunch someone will ask. “Reading” does not survive that question.',
        },
        { type: 'h2', text: 'When the section adds value' },
        {
          type: 'p',
          text: 'It adds if the interest is specific, checkable, and close to the role or the culture they describe. “Club runner, sub-45 10k” says discipline and a number. “I run a product-management book group (20 people, Meetup)” is informal leadership. “Django repo contributions (link)” is a portfolio. The recruiter can Google or ask. That is worth it.',
        },
        {
          type: 'ul',
          items: [
            'Team or registered sport, with one fact (league, race number, captain).',
            'Volunteering with a function and frequency, not “I help an NGO”.',
            'Public projects: blog, GitHub, newsletter, podcast with real episodes.',
            'A hobby that is a sector keyword (photography for a studio, sailing for a yard, chess for an analytical role — with a club or tournament, not the myth).',
            'Leisure languages only if they are not already in Languages.',
          ],
        },
        {
          type: 'p',
          text: 'Before: Travel, film, music, internet, sport. After: Trail running (half marathons 2023–2025) and community radio (sound tech, one show/week). The first is a sticker. The second is a ten-second story.',
        },
        { type: 'h2', text: 'What to cut (even if it is true)' },
        {
          type: 'p',
          text: 'Travel, film, series, music, “time with friends”, social media, “the internet”, generic cooking, football as a spectator. They are not false; they are non-information. Also cut political or religious interests unless that is the job. Be careful with sharp humour or high-risk hobbies in conservative sectors: it is not censorship; the PDF travels to people who do not know you.',
        },
        {
          type: 'p',
          text: 'If you are junior and the page looks short, do not fill with hobbies. Add a project, a dissertation in two lines, an internship. See [internship CV](/blog/cv-para-practicas) and [CV with no experience](/blog/cv-sin-experiencia). Empty space beats five clichés. Related to [what not to put](/blog/que-no-poner-en-el-cv): generic interests are the same kind of noise as marital status.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'If hobbies weigh more than achievements, the file is backwards',
          lead: 'Upload the CV you already have. Cut “travel and film”, leave two defensible interests or none, and regenerate a page you can send.',
          from: 'Travel, film, music',
          to: 'Two lines they can ask about',
          button: 'Trim interests',
        },
        { type: 'h2', text: 'How to write them (no bars, no emoji)' },
        {
          type: 'p',
          text: 'Plain text, at the end, three items max. Name + fact. Do not use “passion 90%” bars. Do not use a bicycle icon: ATS does not index the pictogram. Do not mix interests with skills (“teamwork” is not a hobby). If the interest is a project, a short text URL (github.com/ana) beats a decorative QR that does not parse.',
        },
        {
          type: 'p',
          text: 'The [professional summary](/blog/resumen-profesional-cv) is not the place for hobbies. “Passionate runner” in the first three lines steals the role and the keywords. The summary is role + proof. Running, if at all, goes at the bottom.',
        },
        { type: 'h2', text: 'When to delete the whole section' },
        {
          type: 'p',
          text: 'Delete it if it pushes you over one page (junior/mid) or two (dense senior) while you are cutting achievements. Delete it on an academic CV or MBB consulting if space is gold. Keep it for SMEs, hospitality, sport, informal education, or a very human process (direct email to a studio). Channel beats the generic advice to “humanise the CV”.',
        },
        {
          type: 'p',
          text: 'In ClarCV you upload the PDF, see whether the section is bloated or in icons, and regenerate an ATS template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. You decide whether interests stay; the tool does not invent a trek.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do interests help with ATS?',
              a: 'Almost never. ATS does not search for “hiking”. They help a human if they are specific. Do not stuff them as fake keywords.',
            },
            {
              q: 'Should I list gaming when applying to a game studio?',
              a: 'Yes, with a fact: title, clan role, mod, game jam. “I like video games” no.',
            },
            {
              q: 'What if my hobby is the job (photography, code)?',
              a: 'Better as a project or experience. The interests footer is for what does not fit above.',
            },
            {
              q: 'Three hobbies or one?',
              a: 'One excellent line beats three weak ones. Three max.',
            },
            {
              q: 'Does ClarCV delete hobbies on its own?',
              a: 'You trim. Analysis may flag density and structure; the call is yours.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'referencias-en-el-cv',
    date: '2026-08-22',
    category: 'writing',
    related: ['checklist-cv-antes-de-enviar', 'que-no-poner-en-el-cv', 'enviar-cv-por-email'],
    es: {
      title: 'Referencias en el CV: cuándo ponerlas y el “se facilitarán”',
      description:
        'Por qué “se facilitarán a petición” sobra, cuándo adjuntar un contacto y cómo no quemar a tu jefe actual. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“Referencias se facilitarán a petición” es la frase más repetida y menos útil del currículum español. No es una keyword. No abre entrevistas. Ocupa dos líneas que podrían ser un logro. Quien busca “referencias en el CV” suele tener un Word de 2015 o una plantilla que deja el bloque vacío. La respuesta corta: en la mayoría de procesos privados en España, no las pongas en el PDF. Tenlas listas para cuando las pidan.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre cuándo sí listar un nombre (empleo público, academia, algunos procesos UK), cómo no quemar a tu manager actual, qué datos pedir permiso para compartir, y cómo el espacio recuperado mejora el parseo. Encaja con [qué no poner](/blog/que-no-poner-en-el-cv) y con [enviar el CV por email](/blog/enviar-cv-por-email): las referencias van en un segundo adjunto o en un email posterior, no en la primera página que filtra el ATS.',
        },
        {
          type: 'p',
          text: 'No vas a “esconder” que nadie hablará bien de ti. Vas a no gastar la primera pantalla en un trámite que llega en la oferta verbal. Un teléfono de tu jefe en un PDF que se reenvía a veinte personas es un problema de él y tuyo.',
        },
        { type: 'h2', text: 'Por qué “se facilitarán” sobra' },
        {
          type: 'p',
          text: 'Todo el mundo puede facilitar referencias si el proceso llega ahí. Escribirlo es como escribir “puedo adjuntar el DNI si me lo piden”. El reclutador asume que sí. El ATS no busca esa frase. Lo único que hace es decir “este CV sigue una plantilla antigua”. Córtala. Si el anuncio exige “incluir dos referencias”, entonces sí: bloque al final con nombre, cargo, empresa, email y teléfono, con permiso previo.',
        },
        {
          type: 'ul',
          items: [
            'Proceso privado España / LatAm corporativo: no las pongas; ten 2–3 personas avisadas.',
            'Anuncio que las exige: lista corta al final, no en la cabecera.',
            'Academia / becas / algunos masters: a veces piden cartas, no teléfonos en el CV.',
            'UK: “References available on request” también está en retirada; muchos piden contactos más tarde.',
            'Empleo público: sigue el pliego. Si el formulario pide avales, van al formulario.',
          ],
        },
        { type: 'h2', text: 'Cómo tenerlas listas sin imprimirlas' },
        {
          type: 'p',
          text: 'Elige dos o tres personas que vieron tu trabajo (manager, cliente, profe de prácticas), no a tu madre. Avísales antes de que empiece la ronda. Pasa el puesto y dos logros que pueden confirmar. Pide permiso para compartir email y teléfono. Un documento aparte “Ana_Lopez_referencias.pdf” solo cuando te lo pidan, no como tercer adjunto no solicitado junto al CV.',
        },
        {
          type: 'p',
          text: 'No pongas a tu jefe actual si aún no sabe que te vas, salvo que el proceso sea confidencial y esa persona esté en el secreto. Una llamada de un reclutador a tu mesa el martes es una forma cara de dimitir. Usa un manager anterior o un cliente. Si solo tienes el jefe actual, dilo en la entrevista: “puedo dar referencias tras el ok verbal”. Es estándar.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Esas dos líneas pueden ser un logro',
          lead: 'Quita “se facilitarán a petición”, deja el espacio a un resultado que el ATS sí lee, y regenera el PDF que ya tienes.',
          from: 'Se facilitarán a petición',
          to: 'Una viñeta con cifra',
          button: 'Recuperar el espacio',
        },
        { type: 'h2', text: 'Cartas de recomendación LinkedIn vs el CV' },
        {
          type: 'p',
          text: 'Las recomendaciones de LinkedIn no sustituyen una llamada, pero un reclutador humano a veces las hojea. No las copies enteras al PDF: el ATS las trata como muro de texto. Un “recomendaciones en el perfil” sobra; el enlace de LinkedIn en contacto basta. Alinea fechas y títulos con el perfil: [LinkedIn y CV](/blog/linkedin-y-cv).',
        },
        {
          type: 'p',
          text: 'Antes de enviar, el [checklist](/blog/checklist-cv-antes-de-enviar) no incluye referencias salvo que el anuncio las pida. Sí incluye que el PDF parsea y que el email de asunto está claro.',
        },
        { type: 'h2', text: 'Qué hacer con el PDF que ya las trae' },
        {
          type: 'p',
          text: 'Si tu plantilla de Canva o Word reserva un tercio de página a tres contactos, córtalo. En ClarCV subes el archivo, el análisis mira densidad y estructura, y regeneras una plantilla ATS de una columna. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Conserva los nombres en un nota tuya, no en el documento público.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo “referencias disponibles” en inglés en un CV para Londres?',
              a: 'Puedes omitirlo. Si el anuncio las pide, lista nombres. La frase sola no suma.',
            },
            {
              q: '¿Puedo poner el móvil de un compañero sin avisarle?',
              a: 'No. Quemas la referencia y a ti. Permiso primero.',
            },
            {
              q: '¿Y cartas escaneadas de 2018?',
              a: 'No las adjuntas al CV. Están desactualizadas y pesan. Un contacto vivo vale más.',
            },
            {
              q: '¿El ATS penaliza no poner referencias?',
              a: 'No. No es un campo que suela puntuar. Penaliza el PDF ilegible, no la ausencia de esa frase.',
            },
            {
              q: '¿ClarCV inventa referencias?',
              a: 'No. Ni debe. Quita el bloque decorativo si está en la plantilla; los contactos reales son tuyos.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'References on a CV: when to add them and “available on request”',
      description:
        'Why “available on request” is wasted space, when to attach a contact, and how not to burn your current manager. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“References available on request” is the most repeated and least useful line on a Spanish CV. It is not a keyword. It does not open interviews. It eats two lines that could be an achievement. People search “references on a CV” because a 2015 Word file or a template left the block empty. Short answer: in most private processes in Spain, do not put them on the PDF. Have them ready for when they are asked.',
        },
        {
          type: 'p',
          text: 'This guide covers when to list a name (public sector, academia, some UK processes), how not to burn your current manager, what you need permission to share, and how recovered space improves parsing. It sits with [what not to put](/blog/que-no-poner-en-el-cv) and [emailing the CV](/blog/enviar-cv-por-email): references belong in a second attachment or a later email, not on the first page the ATS filters.',
        },
        {
          type: 'p',
          text: 'You are not “hiding” that nobody will speak well of you. You are not spending the first screen on a step that comes at verbal offer. Your boss’s phone on a PDF forwarded to twenty people is a problem for them and for you.',
        },
        { type: 'h2', text: 'Why “available on request” is surplus' },
        {
          type: 'p',
          text: 'Everyone can provide references if the process gets that far. Writing it is like writing “I can attach my ID if asked”. The recruiter assumes yes. ATS does not search for that sentence. All it does is say “this CV follows an old template”. Cut it. If the ad requires “include two references”, then yes: a short block at the end with name, title, company, email, and phone, with prior permission.',
        },
        {
          type: 'ul',
          items: [
            'Private process Spain / corporate LatAm: do not list them; keep 2–3 people briefed.',
            'Ad that requires them: short list at the end, not in the header.',
            'Academia / grants / some master’s: they often want letters, not phones on the CV.',
            'UK: “available on request” is fading too; many ask for contacts later.',
            'Public employment: follow the spec. If the form wants endorsements, they go on the form.',
          ],
        },
        { type: 'h2', text: 'How to have them ready without printing them' },
        {
          type: 'p',
          text: 'Pick two or three people who saw your work (manager, client, internship tutor), not your parent. Brief them before the round starts. Share the role and two achievements they can confirm. Ask permission to share email and phone. A separate “Ana_Lopez_references.pdf” only when asked, not as a third unsolicited attachment with the CV.',
        },
        {
          type: 'p',
          text: 'Do not list your current boss if they do not know you are leaving, unless the process is confidential and they are in on it. A recruiter calling your desk on Tuesday is an expensive way to resign. Use a former manager or a client. If you only have the current boss, say it in interview: “I can provide references after a verbal yes”. That is standard.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Those two lines can be an achievement',
          lead: 'Drop “available on request”, give the space to a result ATS can read, and regenerate the PDF you already have.',
          from: 'Available on request',
          to: 'A bullet with a number',
          button: 'Reclaim the space',
        },
        { type: 'h2', text: 'LinkedIn recommendations vs the CV' },
        {
          type: 'p',
          text: 'LinkedIn recommendations do not replace a call, but a human recruiter sometimes skims them. Do not paste them in full on the PDF: ATS treats them as a wall of text. “See recommendations on my profile” is surplus; the LinkedIn URL in contact is enough. Align dates and titles with the profile: [LinkedIn and CV](/blog/linkedin-y-cv).',
        },
        {
          type: 'p',
          text: 'Before sending, the [checklist](/blog/checklist-cv-antes-de-enviar) does not include references unless the ad asked. It does include that the PDF parses and that the email subject is clear.',
        },
        { type: 'h2', text: 'What to do with a PDF that already has them' },
        {
          type: 'p',
          text: 'If your Canva or Word template reserves a third of a page for three contacts, cut it. In ClarCV you upload the file, analysis looks at density and structure, and you regenerate a one-column ATS template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. Keep the names in your own note, not on the public document.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I put “references available” in English for a London CV?',
              a: 'You can omit it. If the ad asks, list names. The sentence alone adds nothing.',
            },
            {
              q: 'Can I put a colleague’s mobile without telling them?',
              a: 'No. You burn the reference and yourself. Permission first.',
            },
            {
              q: 'What about scanned 2018 letters?',
              a: 'Do not attach them to the CV. They are stale and heavy. A live contact is worth more.',
            },
            {
              q: 'Does ATS penalise missing references?',
              a: 'No. It is not a field it usually scores. It penalises an unreadable PDF, not the absence of that phrase.',
            },
            {
              q: 'Does ClarCV invent references?',
              a: 'No. And it must not. It can drop a decorative block if it is in the template; real contacts are yours.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
